import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { ClipboardList } from 'lucide-react'
import { displayTicketNo } from './KOTCard'
import type { Order, OrderItem } from '@/types'

/** A line still needs cooking if it isn't prepared/handed-off/removed. */
function needsPrep(item: OrderItem): boolean {
  if (item.status === 'voided' || item.status === 'draft') return false
  if (item.status === 'ready' || item.status === 'served') return false
  return true
}

export interface AllDayLine {
  name: string
  qty: number
  contributions: { ticketNo: string; qty: number }[]
}

/**
 * "All-Day" aggregation: total the un-prepared line quantities across all active
 * tickets so a station can batch-prep ("6× Wings working") instead of scanning
 * every ticket. This is the useful core of the old Prep Queue rail, lifted into
 * a shared hook so a compact header toggle (and any future view) can reuse it.
 */
export function useAllDayCounts(orders: (Order & { items?: OrderItem[] })[]): {
  lines: AllDayLine[]
  totalQty: number
} {
  return useMemo(() => {
    const map = new Map<string, AllDayLine>()
    for (const o of orders) {
      const ticketNo = displayTicketNo(o.order_number)
      for (const it of o.items ?? []) {
        if (!needsPrep(it)) continue
        const name = it.product?.name ?? 'Unknown'
        const cur = map.get(name)
        if (cur) {
          cur.qty += it.quantity
          const existing = cur.contributions.find((c) => c.ticketNo === ticketNo)
          if (existing) existing.qty += it.quantity
          else cur.contributions.push({ ticketNo, qty: it.quantity })
        } else {
          map.set(name, { name, qty: it.quantity, contributions: [{ ticketNo, qty: it.quantity }] })
        }
      }
    }
    const lines = [...map.values()].sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name))
    return { lines, totalQty: lines.reduce((sum, l) => sum + l.qty, 0) }
  }, [orders])
}

/**
 * Compact dropdown panel of the All-Day counts, anchored under the header
 * toggle. Deliberately lightweight (no per-ticket expansion) so it works on a
 * small KDS tablet — the goal is a glanceable "what's on the fire" batch list.
 */
export function AllDayPanel({
  orders,
  onClose,
}: {
  orders: (Order & { items?: OrderItem[] })[]
  onClose: () => void
}) {
  const { lines, totalQty } = useAllDayCounts(orders)
  return (
    <div className="relative z-40">
      <div
        className="absolute right-4 top-2 max-h-[60vh] w-[min(18rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-xl"
        role="dialog"
        aria-label="All-day prep counts"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <ClipboardList className="h-4 w-4" />
            All-Day
            {totalQty > 0 && (
              <Badge variant="outline" className="ml-1 font-mono text-[11px]">
                {totalQty} item{totalQty !== 1 ? 's' : ''}
              </Badge>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Close all-day panel"
          >
            ×
          </button>
        </div>
        {lines.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">Nothing on the fire.</p>
        ) : (
          <ul className="divide-y divide-border">
            {lines.map((line) => (
              <li key={line.name} className="flex items-center gap-2 py-2">
                <span className="inline-flex h-6 min-w-[2rem] items-center justify-center rounded-md bg-primary px-1.5 text-xs font-bold tabular-nums text-primary-foreground">
                  {line.qty}
                </span>
                <span className="truncate text-sm font-medium text-foreground">
                  {line.name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
